import React, { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { searchAPI } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Search,
  BookOpen,
  FileText,
  CheckSquare,
  ArrowRight,
  Layers,
} from "lucide-react";

const TYPE_ICONS = {
  module: Layers,
  learning: BookOpen,
  template: FileText,
  checklist: CheckSquare,
};

const TYPE_LABELS = {
  module: "Module",
  learning: "Learning",
  template: "Template",
  checklist: "Checklist",
};

export default function SearchPage() {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  const debounceRef = React.useRef(null);

  const handleSearch = useCallback(async (q) => {
    if (q.length < 2) {
      setResults([]);
      setSearched(false);
      return;
    }
    setLoading(true);
    setSearched(true);
    try {
      const res = await searchAPI.search(q);
      setResults(res.data);
    } catch (err) {
      console.error("Search failed:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  const onInputChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => handleSearch(val), 300);
  };

  return (
    <div className="space-y-6" data-testid="search-page">
      <div>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight">
          Knowledge Base
        </h1>
        <p className="text-slate-500 mt-2 text-base font-body">
          Search across all modules, templates, checklists, and learning content.
        </p>
      </div>

      {/* Search Input */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          data-testid="search-input"
          value={query}
          onChange={onInputChange}
          placeholder="Search for topics, templates, or concepts..."
          className="pl-10 h-12 rounded-sm border-slate-200 focus-visible:ring-slate-900 text-sm bg-white"
        />
      </div>

      {/* Results */}
      {loading && (
        <div className="text-sm text-slate-400 py-4">Searching...</div>
      )}

      {!loading && searched && results.length === 0 && (
        <div className="bg-white border border-slate-200 rounded-sm p-8 text-center">
          <Search className="w-10 h-10 mx-auto text-slate-300 mb-3" />
          <p className="text-slate-500 text-sm">
            No results found for "{query}". Try different keywords.
          </p>
        </div>
      )}

      {!loading && results.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold">
            {results.length} result{results.length !== 1 ? "s" : ""}
          </p>
          {results.map((result, idx) => {
            const Icon = TYPE_ICONS[result.type] || BookOpen;
            return (
              <button
                key={idx}
                data-testid={`search-result-${idx}`}
                onClick={() => navigate(`/modules/${result.module_id}`)}
                className="w-full text-left bg-white border border-slate-200 rounded-sm p-4 hover:border-slate-300 hover:shadow-sm transition-all duration-200 flex items-start gap-3"
              >
                <div className="w-8 h-8 bg-slate-100 rounded-sm flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Icon className="w-4 h-4 text-slate-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="text-sm font-semibold text-slate-900 truncate">
                      {result.title}
                    </h3>
                    <Badge variant="secondary" className="text-xs flex-shrink-0">
                      {TYPE_LABELS[result.type]}
                    </Badge>
                  </div>
                  <p className="text-xs text-slate-500 mb-1">
                    Module {result.module_id}: {result.module_title}
                  </p>
                  <p className="text-sm text-slate-600 line-clamp-2">{result.snippet}</p>
                </div>
                <ArrowRight className="w-4 h-4 text-slate-400 flex-shrink-0 mt-1" />
              </button>
            );
          })}
        </div>
      )}

      {/* Quick Links when no search */}
      {!searched && (
        <div>
          <h2 className="font-heading text-lg font-semibold text-slate-900 mb-3">
            Popular Topics
          </h2>
          <div className="flex flex-wrap gap-2">
            {[
              "retainer search",
              "boolean search",
              "fee negotiation",
              "STAR method",
              "talent mapping",
              "counteroffer",
              "guarantee period",
              "client briefing",
              "candidate profile",
              "compensation benchmarking",
            ].map((topic) => (
              <button
                key={topic}
                data-testid={`quick-search-${topic.replace(/\s/g, "-")}`}
                onClick={() => {
                  setQuery(topic);
                  handleSearch(topic);
                }}
                className="px-3 py-1.5 border border-slate-200 rounded-sm text-sm text-slate-600 hover:bg-white hover:border-slate-300 hover:text-slate-900 transition-all duration-200"
              >
                {topic}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
